import { useState } from "react";
import { Heart, HandHelping, Package, Loader2, Check, BookOpen } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DonateDialog from "@/components/DonateDialog";
import EnrollDialog from "@/components/EnrollDialog";
import { toast } from "sonner";
import { submitVolunteer, submitGoods } from "@/lib/api";
import { IMG, VOLUNTEER_INTERESTS, GOODS_CATEGORIES, IMPACT_ANCHORS, MINISTRY } from "@/lib/content";

function VolunteerForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", skills: "", availability: "", interests: [], message: "" });
  const [loading, setLoading] = useState(false);

  const toggle = (i) => setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try { const res = await submitVolunteer(form); toast.success(res.message); setForm({ name: "", email: "", phone: "", skills: "", availability: "", interests: [], message: "" }); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="volunteer-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-plum text-sm">Full name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vol-name" className="h-12 bg-white border-plum/15 mt-1" /></div>
        <div><Label className="text-plum text-sm">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vol-email" className="h-12 bg-white border-plum/15 mt-1" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-plum text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vol-phone" className="h-12 bg-white border-plum/15 mt-1" /></div>
        <div><Label className="text-plum text-sm">Availability</Label><Input placeholder="e.g. weekends, evenings" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} data-testid="vol-availability" className="h-12 bg-white border-plum/15 mt-1" /></div>
      </div>
      <div>
        <Label className="text-plum text-sm mb-2 block">I'm interested in</Label>
        <div className="flex flex-wrap gap-2">
          {VOLUNTEER_INTERESTS.map((i) => (
            <button type="button" key={i} onClick={() => toggle(i)} data-testid={`vol-interest-${i.split(" ")[0].toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm border transition-all ${form.interests.includes(i) ? "bg-plum text-cream border-plum" : "bg-white text-plum border-plum/20 hover:border-gold"}`}>
              {form.interests.includes(i) && <Check size={13} className="inline mr-1" />}{i}
            </button>
          ))}
        </div>
      </div>
      <div><Label className="text-plum text-sm">Skills / trades you can offer</Label><Input placeholder="e.g. carpentry, painting, grant writing, mentoring" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} data-testid="vol-skills" className="h-12 bg-white border-plum/15 mt-1" /></div>
      <div><Label className="text-plum text-sm">Anything else?</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="vol-message" rows={3} className="bg-white border-plum/15 mt-1" /></div>
      <button type="submit" disabled={loading} data-testid="vol-submit-btn" className="bg-plum hover:bg-plum-dark text-cream font-semibold rounded-full px-8 py-4 flex items-center gap-2 transition-colors disabled:opacity-60">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <HandHelping size={18} />}{loading ? "Submitting..." : "Offer My Time"}
      </button>
    </form>
  );
}

function GoodsForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "vehicle", description: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try { const res = await submitGoods(form); toast.success(res.message); setForm({ name: "", email: "", phone: "", category: "vehicle", description: "" }); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="goods-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-plum text-sm">Full name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="goods-name" className="h-12 bg-white border-plum/15 mt-1" /></div>
        <div><Label className="text-plum text-sm">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="goods-email" className="h-12 bg-white border-plum/15 mt-1" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-plum text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="goods-phone" className="h-12 bg-white border-plum/15 mt-1" /></div>
        <div>
          <Label className="text-plum text-sm">What are you donating?</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="h-12 bg-white border-plum/15 mt-1" data-testid="goods-category"><SelectValue /></SelectTrigger>
            <SelectContent>{GOODS_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label className="text-plum text-sm">Tell us about your donation</Label><Textarea placeholder="Describe the item(s), condition, and any details." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="goods-description" rows={4} className="bg-white border-plum/15 mt-1" /></div>
      <button type="submit" disabled={loading} data-testid="goods-submit-btn" className="bg-plum hover:bg-plum-dark text-cream font-semibold rounded-full px-8 py-4 flex items-center gap-2 transition-colors disabled:opacity-60">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Package size={18} />}{loading ? "Submitting..." : "Pledge My Gift"}
      </button>
    </form>
  );
}

export default function GetInvolved() {
  return (
    <>
      <PageHero testid="involved-hero" kicker="Get Involved" title="Give. Serve. Provide. Change a life." subtitle="There are many ways to partner with Greater Faith — choose the one that fits your heart and your gifts." image={IMG.backpacks} />

      <section className="py-20 sm:py-28 relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="give" className="w-full">
            <TabsList className="grid grid-cols-3 w-full max-w-xl mx-auto bg-plum/5 rounded-full p-1.5 h-auto" data-testid="involved-tabs">
              <TabsTrigger value="give" data-testid="tab-give" className="rounded-full py-2.5 data-[state=active]:bg-plum data-[state=active]:text-cream text-plum font-medium">Give</TabsTrigger>
              <TabsTrigger value="volunteer" data-testid="tab-volunteer" className="rounded-full py-2.5 data-[state=active]:bg-plum data-[state=active]:text-cream text-plum font-medium">Volunteer</TabsTrigger>
              <TabsTrigger value="goods" data-testid="tab-goods" className="rounded-full py-2.5 data-[state=active]:bg-plum data-[state=active]:text-cream text-plum font-medium">Donate Goods</TabsTrigger>
            </TabsList>

            <TabsContent value="give" className="mt-12">
              <Reveal>
                <div className="text-center max-w-2xl mx-auto">
                  <Heart className="text-gold-dark mx-auto mb-4" size={40} />
                  <h2 className="font-heading text-3xl sm:text-4xl text-plum font-semibold">Every gift becomes hope</h2>
                  <p className="text-stone mt-4 text-lg">See exactly what your generosity provides, then give in a moment.</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
                  {IMPACT_ANCHORS.map((a) => (
                    <div key={a.amount} className="bg-white border border-plum/10 rounded-2xl p-6 lift text-center">
                      <p className="font-heading text-4xl text-plum font-semibold">${a.amount}</p>
                      <p className="text-stone text-sm mt-2 leading-relaxed">{a.label}</p>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-10">
                  <DonateDialog trigger={<button className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-10 py-4 text-lg flex items-center gap-2 mx-auto transition-all hover:-translate-y-1" data-testid="give-donate-btn"><Heart size={20} /> Donate Now</button>} />
                  <p className="text-stone mt-5 text-sm">Prefer Cash App? Send your gift to <span className="font-heading text-lg text-plum font-semibold">{MINISTRY.cashApp}</span> &mdash; or bless us with church &amp; household items.</p>
                </div>
              </Reveal>
            </TabsContent>

            <TabsContent value="volunteer" className="mt-12">
              <Reveal>
                <div className="max-w-2xl mx-auto text-center mb-10">
                  <HandHelping className="text-gold-dark mx-auto mb-4" size={40} />
                  <h2 className="font-heading text-3xl sm:text-4xl text-plum font-semibold">Lend your time and talent</h2>
                  <p className="text-stone mt-4 text-lg">From renovating the foster care home to mentoring young women, your skills are needed.</p>
                </div>
                <div className="bg-cream border border-plum/10 rounded-2xl p-6 sm:p-8 max-w-3xl mx-auto"><VolunteerForm /></div>
              </Reveal>
            </TabsContent>

            <TabsContent value="goods" className="mt-12">
              <Reveal>
                <div className="max-w-2xl mx-auto text-center mb-10">
                  <Package className="text-gold-dark mx-auto mb-4" size={40} />
                  <h2 className="font-heading text-3xl sm:text-4xl text-plum font-semibold">Donate vehicles, homes & goods</h2>
                  <p className="text-stone mt-4 text-lg">Cars change futures. Furniture builds homes. Supplies equip children. Pledge your in-kind gift below.</p>
                </div>
                <div className="bg-cream border border-plum/10 rounded-2xl p-6 sm:p-8 max-w-3xl mx-auto"><GoodsForm /></div>
              </Reveal>
            </TabsContent>
          </Tabs>

          {/* Prayer enrollment */}
          <Reveal className="mt-20">
            <div className="bg-plum-dark rounded-3xl p-10 sm:p-14 text-center text-cream">
              <BookOpen className="text-gold mx-auto mb-4" size={40} />
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold">Intercessory Prayer Class Enrollment</h2>
              <p className="text-cream/80 mt-4 text-lg max-w-xl mx-auto">&ldquo;Lord, teach us to pray.&rdquo; Enroll in our intercessory prayer class and grow in the power of prayer.</p>
              <div className="mt-8">
                <EnrollDialog trigger={<button className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-8 py-4 flex items-center gap-2 mx-auto transition-all hover:-translate-y-1" data-testid="involved-enroll-btn"><BookOpen size={18} /> Enroll in Prayer Class</button>} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
